import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  ArrowLeft, DollarSign, Users, CheckCircle2, AlertCircle, Clock,
  FileText, Download, RefreshCw, ChevronRight, Building2, Play,
  ShieldCheck, CreditCard, Stamp, RotateCcw, XCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

const STATUS_CONFIG = {
  borrador:         { label: "Borrador",         color: "bg-slate-100 text-slate-700", step: 0 },
  calculado:        { label: "Calculado",        color: "bg-blue-100 text-blue-800",   step: 1 },
  autorizado:       { label: "Autorizado",       color: "bg-amber-100 text-amber-800", step: 2 },
  pagado:           { label: "Pagado",           color: "bg-emerald-100 text-emerald-800", step: 3 },
  timbrado:         { label: "Timbrado",         color: "bg-green-100 text-green-800", step: 4 },
  timbrado_parcial: { label: "Timbrado parcial", color: "bg-orange-100 text-orange-800", step: 4 },
  cerrado:          { label: "Cerrado",          color: "bg-gray-100 text-gray-600",   step: 5 },
};

const CFDI_COLORS = {
  pending:  "bg-slate-100 text-slate-600",
  timbrado: "bg-green-100 text-green-800",
  error:    "bg-red-100 text-red-700",
  cancelado:"bg-gray-100 text-gray-600",
};

const STEPS = ["Borrador", "Calculado", "Autorizado", "Pagado", "Timbrado", "Cerrado"];

function fmt(n) {
  return (n ?? 0).toLocaleString("es-MX", { style: "currency", currency: "MXN" });
}

function ConfirmModal({ title, message, onConfirm, onCancel, loading }) {
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl p-6 max-w-md w-full mx-4">
        <h3 className="text-lg font-bold text-slate-900 mb-2">{title}</h3>
        <p className="text-sm text-slate-600 mb-6">{message}</p>
        <div className="flex justify-end gap-3">
          <Button variant="outline" onClick={onCancel} disabled={loading}>Cancelar</Button>
          <Button onClick={onConfirm} disabled={loading} className="bg-[#FFDF00] text-slate-900 hover:bg-yellow-400 font-semibold">
            {loading ? "Procesando..." : "Confirmar"}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function OpsRRHHNominaDetalle() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [period, setPeriod] = useState(null);
  const [runs, setRuns] = useState([]);
  const [runsTotal, setRunsTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [tab, setTab] = useState("resumen");
  const [modal, setModal] = useState(null);
  const [runsPage, setRunsPage] = useState(1);
  const [cfdiFilter, setCfdiFilter] = useState("");

  const isCorporate = user?.role === "corporate_admin";
  const isHR = ["hr_admin", "corporate_admin"].includes(user?.role);
  const isTreasury = ["treasury", "corporate_admin"].includes(user?.role);
  const isAllPayroll = ["hr_admin", "treasury", "corporate_admin"].includes(user?.role);

  const loadPeriod = useCallback(async () => {
    try {
      const r = await api.get(`/ops/payroll/periods/${id}`);
      setPeriod(r.data);
    } catch {
      toast.error("Error al cargar el periodo");
    }
  }, [id]);

  const loadRuns = useCallback(async () => {
    try {
      const params = new URLSearchParams({ page: runsPage, limit: 100 });
      if (cfdiFilter) params.set("cfdi_status", cfdiFilter);
      const r = await api.get(`/ops/payroll/periods/${id}/runs?${params}`);
      setRuns(r.data.runs || []);
      setRunsTotal(r.data.total || 0);
    } catch {
      toast.error("Error al cargar los cálculos");
    }
  }, [id, runsPage, cfdiFilter]);

  useEffect(() => {
    setLoading(true);
    Promise.all([loadPeriod(), loadRuns()]).finally(() => setLoading(false));
  }, [loadPeriod, loadRuns]);

  const doAction = async (action, label) => {
    setActionLoading(true);
    try {
      await api.post(`/ops/payroll/periods/${id}/${action}`);
      toast.success(`${label} exitoso`);
      await Promise.all([loadPeriod(), loadRuns()]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Error al ${label.toLowerCase()}`);
    } finally {
      setActionLoading(false);
      setModal(null);
    }
  };

  const downloadBBVA = async () => {
    try {
      const r = await api.get(`/ops/payroll/periods/${id}/bank-layout.txt`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "text/plain" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `bbva_layout_${period.period_number}.txt`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Error al generar el layout BBVA");
    }
  };

  const downloadXLSX = async () => {
    try {
      const r = await api.get(`/ops/payroll/periods/${id}/export.xlsx`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `nomina_${period.period_number}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Error al exportar Excel");
    }
  };

  const retryStamp = async (runId) => {
    try {
      await api.post(`/ops/payroll/runs/${runId}/retry-stamp`);
      toast.success("Retimbrado exitoso");
      await Promise.all([loadPeriod(), loadRuns()]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al retimbrar");
    }
  };

  if (loading) return <div className="p-6 text-slate-400 text-center">Cargando...</div>;
  if (!period) return <div className="p-6 text-slate-400">Periodo no encontrado</div>;

  const sc = STATUS_CONFIG[period.status] || STATUS_CONFIG.borrador;
  const stepIdx = sc.step;
  const totals = period.totals || {};

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate("/ops/rrhh/nomina")}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <DollarSign className="w-6 h-6 text-[#FFDF00]" />
              {period.period_number}
            </h1>
            <p className="text-sm text-slate-500 mt-0.5">{period.legal_entity_name} · {period.period_type}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${sc.color}`} data-testid="period-status-badge">
            {sc.label}
          </span>
          <Button variant="ghost" size="icon" onClick={() => Promise.all([loadPeriod(), loadRuns()])}>
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Timeline */}
      <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm" data-testid="period-timeline">
        <div className="flex items-center gap-0">
          {STEPS.map((s, i) => (
            <div key={s} className="flex items-center flex-1">
              <div className={`flex flex-col items-center flex-1 ${i <= stepIdx ? "text-slate-900" : "text-slate-400"}`}>
                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold border-2 ${
                  i < stepIdx ? "bg-green-500 border-green-500 text-white"
                  : i === stepIdx ? "bg-[#FFDF00] border-yellow-400 text-slate-900"
                  : "bg-white border-slate-200 text-slate-400"
                }`}>{i + 1}</div>
                <span className="text-xs mt-1 text-center leading-tight">{s}</span>
              </div>
              {i < STEPS.length - 1 && (
                <div className={`h-0.5 flex-1 -mt-4 ${i < stepIdx ? "bg-green-400" : "bg-slate-200"}`} />
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-slate-200">
        {[
          { key: "resumen", label: "Resumen" },
          { key: "empleados", label: `Empleados (${totals.employees_count ?? 0})` },
          { key: "pagos", label: "Pagos" },
          { key: "cfdis", label: "CFDIs" },
        ].map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
              tab === t.key
                ? "border-[#FFDF00] text-slate-900"
                : "border-transparent text-slate-500 hover:text-slate-700"
            }`}
            data-testid={`tab-${t.key}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* TAB: Resumen */}
      {tab === "resumen" && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          {/* Info */}
          <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm space-y-3">
            <h3 className="font-semibold text-slate-800">Informacion del periodo</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-slate-500">Razon social</span><span className="font-medium">{period.legal_entity_name}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Tipo</span><span className="capitalize">{period.period_type}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Inicio</span><span>{period.start_date}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Fin</span><span>{period.end_date}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Fecha pago</span><span className="font-semibold">{period.payment_date}</span></div>
              {period.notes && <p className="text-slate-500 text-xs italic">{period.notes}</p>}
            </div>
          </div>

          {/* Totales */}
          <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm space-y-3">
            <h3 className="font-semibold text-slate-800">Totales del periodo</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-slate-500">Empleados</span><span className="font-semibold">{totals.employees_count ?? 0}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Total bruto</span><span className="font-mono">{fmt(totals.gross_total)}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Deducciones</span><span className="font-mono text-red-600">-{fmt(totals.deductions_total)}</span></div>
              <div className="flex justify-between border-t pt-2"><span className="font-semibold text-slate-800">Neto a pagar</span><span className="font-mono font-bold text-emerald-700">{fmt(totals.net_total)}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Costos patron</span><span className="font-mono text-orange-600">{fmt(totals.employer_taxes_total)}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Bonos KPI</span><span className="font-mono">{fmt(totals.total_kpi_bonus)}</span></div>
            </div>
          </div>

          {/* Acciones */}
          <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm space-y-3">
            <h3 className="font-semibold text-slate-800">Acciones</h3>
            <div className="space-y-2">
              {period.status === "borrador" && isHR && (
                <Button
                  className="w-full bg-blue-600 text-white hover:bg-blue-700 font-semibold"
                  onClick={() => setModal({ action: "calculate", label: "Calcular nómina", message: `Se calculará la nómina para todos los colaboradores activos de ${period.legal_entity_name}. ¿Confirmas?` })}
                  data-testid="btn-calculate"
                >
                  <Play className="w-4 h-4 mr-2" /> Calcular nómina
                </Button>
              )}
              {period.status === "calculado" && isHR && (
                <Button
                  className="w-full bg-blue-600 text-white hover:bg-blue-700 font-semibold"
                  onClick={() => setModal({ action: "calculate", label: "Recalcular nómina", message: `Se recalculará la nómina reemplazando los cálculos existentes. ¿Confirmas?` })}
                  data-testid="btn-recalculate"
                >
                  <Play className="w-4 h-4 mr-2" /> Recalcular nómina
                </Button>
              )}
              {period.status === "calculado" && isCorporate && (
                <Button
                  className="w-full bg-amber-500 text-white hover:bg-amber-600 font-semibold"
                  onClick={() => setModal({ action: "authorize", label: "Autorizar período", message: `Se autorizará el período ${period.period_number}. Una vez autorizado no podrás editar los cálculos. ¿Confirmas?` })}
                  data-testid="btn-authorize"
                >
                  <ShieldCheck className="w-4 h-4 mr-2" /> Autorizar
                </Button>
              )}
              {period.status === "autorizado" && isTreasury && (
                <>
                  <Button
                    className="w-full bg-emerald-600 text-white hover:bg-emerald-700 font-semibold"
                    onClick={() => setModal({ action: "mark-paid", label: "Marcar como pagado", message: `Se marcará el período como pagado. ¿Confirmas?` })}
                    data-testid="btn-mark-paid"
                  >
                    <CreditCard className="w-4 h-4 mr-2" /> Marcar pagado
                  </Button>
                  <Button variant="outline" className="w-full" onClick={downloadBBVA} data-testid="btn-bbva">
                    <Download className="w-4 h-4 mr-2" /> Descargar layout BBVA
                  </Button>
                </>
              )}
              {(period.status === "pagado" || period.status === "timbrado_parcial") && isAllPayroll && (
                <Button
                  className="w-full bg-green-700 text-white hover:bg-green-800 font-semibold"
                  onClick={() => setModal({
                    action: "stamp",
                    label: "Timbrar CFDIs",
                    message: `Se timbrarán los CFDIs de nómina para ${totals.employees_count ?? 0} colaboradores. Acción irreversible. ¿Confirmas?`
                  })}
                  data-testid="btn-stamp"
                >
                  <Stamp className="w-4 h-4 mr-2" /> Timbrar CFDIs
                </Button>
              )}
              {isAllPayroll && period.status !== "borrador" && (
                <Button variant="outline" className="w-full" onClick={downloadXLSX} data-testid="btn-export">
                  <Download className="w-4 h-4 mr-2" /> Exportar Excel
                </Button>
              )}
              {isTreasury && ["autorizado", "pagado", "timbrado", "timbrado_parcial", "cerrado"].includes(period.status) && (
                <Button variant="outline" className="w-full" onClick={downloadBBVA} data-testid="btn-bbva-2">
                  <Download className="w-4 h-4 mr-2" /> Layout BBVA
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* TAB: Empleados */}
      {tab === "empleados" && (
        <div className="space-y-4">
          {runs.length === 0 ? (
            <div className="text-center py-12 text-slate-400">
              <Users className="w-10 h-10 mx-auto mb-3 text-slate-300" />
              <p>Sin cálculos. Ejecuta la acción "Calcular nómina" primero.</p>
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b">
                  <tr>
                    <th className="text-left px-4 py-3 text-slate-700 font-semibold"># Empleado</th>
                    <th className="text-left px-4 py-3 text-slate-700 font-semibold">Nombre</th>
                    <th className="text-right px-4 py-3 text-slate-700 font-semibold">Dias</th>
                    <th className="text-right px-4 py-3 text-slate-700 font-semibold">Percepciones</th>
                    <th className="text-right px-4 py-3 text-slate-700 font-semibold">Deducciones</th>
                    <th className="text-right px-4 py-3 text-slate-700 font-semibold">Neto</th>
                    <th className="text-right px-4 py-3 text-slate-700 font-semibold">KPI%</th>
                    <th className="text-right px-4 py-3 text-slate-700 font-semibold">Bono KPI</th>
                    <th className="text-left px-4 py-3 text-slate-700 font-semibold">CFDI</th>
                    <th className="px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {runs.map((r, idx) => (
                    <tr
                      key={r.id}
                      className={`border-b hover:bg-slate-50 cursor-pointer ${idx % 2 === 0 ? "" : "bg-slate-50/30"}`}
                      onClick={() => navigate(`/ops/rrhh/nomina/runs/${r.id}`)}
                      data-testid={`run-row-${r.id}`}
                    >
                      <td className="px-4 py-2.5 font-mono text-xs">{r.employee_number}</td>
                      <td className="px-4 py-2.5 font-medium">{r.employee_name}</td>
                      <td className="px-4 py-2.5 text-right text-slate-600">{r.days_worked}/{r.days_in_period}</td>
                      <td className="px-4 py-2.5 text-right font-mono">{fmt(r.total_percepciones)}</td>
                      <td className="px-4 py-2.5 text-right font-mono text-red-600">-{fmt(r.total_deducciones)}</td>
                      <td className="px-4 py-2.5 text-right font-mono font-semibold text-emerald-700">{fmt(r.neto_pagar)}</td>
                      <td className="px-4 py-2.5 text-right text-slate-600">{r.kpi_achievement_pct?.toFixed(1) ?? "-"}%</td>
                      <td className="px-4 py-2.5 text-right font-mono">
                        <span className="inline-flex items-center gap-1 justify-end">
                          {fmt(r.kpi_bonus_amount)}
                          {r.kpi_bonus_note && (
                            <AlertCircle className="w-3.5 h-3.5 text-amber-500 shrink-0" data-testid={`kpi-note-icon-${r.id}`} title={r.kpi_bonus_note} />
                          )}
                        </span>
                      </td>
                      <td className="px-4 py-2.5">
                        <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${CFDI_COLORS[r.cfdi_n_status] || "bg-slate-100 text-slate-600"}`}>
                          {r.cfdi_n_status}
                        </span>
                      </td>
                      <td className="px-4 py-2.5">
                        <ChevronRight className="w-3.5 h-3.5 text-slate-400" />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {runsTotal > 100 && (
            <div className="flex justify-between items-center text-sm text-slate-500">
              <span>{runsTotal} cálculos</span>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => setRunsPage(p => Math.max(1, p - 1))} disabled={runsPage === 1}>Anterior</Button>
                <Button variant="outline" size="sm" onClick={() => setRunsPage(p => p + 1)} disabled={runsPage * 100 >= runsTotal}>Siguiente</Button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* TAB: Pagos */}
      {tab === "pagos" && (
        <div className="space-y-4">
          {isTreasury && (
            <Button variant="outline" onClick={downloadBBVA} data-testid="pagos-bbva-btn">
              <Download className="w-4 h-4 mr-2" /> Descargar layout BBVA
            </Button>
          )}
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b">
                <tr>
                  <th className="text-left px-4 py-3 text-slate-700 font-semibold">Empleado</th>
                  <th className="text-right px-4 py-3 text-slate-700 font-semibold">Neto</th>
                  <th className="text-left px-4 py-3 text-slate-700 font-semibold">Metodo</th>
                  <th className="text-left px-4 py-3 text-slate-700 font-semibold">Referencia</th>
                  <th className="text-left px-4 py-3 text-slate-700 font-semibold">Pagado</th>
                </tr>
              </thead>
              <tbody>
                {runs.map((r, idx) => (
                  <tr key={r.id} className={`border-b ${idx % 2 === 0 ? "" : "bg-slate-50/30"}`}>
                    <td className="px-4 py-2.5 font-medium">{r.employee_name}</td>
                    <td className="px-4 py-2.5 text-right font-mono font-semibold">{fmt(r.neto_pagar)}</td>
                    <td className="px-4 py-2.5 capitalize text-slate-600">{r.payment_method || "transferencia"}</td>
                    <td className="px-4 py-2.5 font-mono text-xs text-slate-500">{r.payment_reference || "-"}</td>
                    <td className="px-4 py-2.5">
                      {r.paid_at
                        ? <span className="text-green-600 text-xs font-medium">{r.paid_at.slice(0, 10)}</span>
                        : <span className="text-slate-400 text-xs">Pendiente</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* TAB: CFDIs */}
      {tab === "cfdis" && (
        <div className="space-y-4">
          <div className="flex gap-3 items-center">
            <select
              className="border rounded-md px-3 py-2 text-sm"
              value={cfdiFilter}
              onChange={e => { setCfdiFilter(e.target.value); setRunsPage(1); }}
            >
              <option value="">Todos los estados</option>
              <option value="pending">Pendiente</option>
              <option value="timbrado">Timbrado</option>
              <option value="error">Error</option>
            </select>
            {period.stamping_errors?.length > 0 && isAllPayroll && (
              <Button
                variant="outline"
                className="text-orange-600 border-orange-200"
                onClick={() => setModal({ action: "stamp", label: "Reintentar timbrado", message: `Se reintentará el timbrado para ${period.stamping_errors.length} CFDIs con error. ¿Confirmas?` })}
                data-testid="btn-retry-all"
              >
                <RotateCcw className="w-4 h-4 mr-2" /> Reintentar errores
              </Button>
            )}
          </div>
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b">
                <tr>
                  <th className="text-left px-4 py-3 text-slate-700 font-semibold">Empleado</th>
                  <th className="text-left px-4 py-3 text-slate-700 font-semibold">UUID Fiscal</th>
                  <th className="text-left px-4 py-3 text-slate-700 font-semibold">Estado</th>
                  <th className="text-left px-4 py-3 text-slate-700 font-semibold">Fecha timbrado</th>
                  <th className="text-left px-4 py-3 text-slate-700 font-semibold">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {runs.map((r, idx) => (
                  <tr key={r.id} className={`border-b ${idx % 2 === 0 ? "" : "bg-slate-50/30"}`}>
                    <td className="px-4 py-2.5 font-medium">{r.employee_name}</td>
                    <td className="px-4 py-2.5 font-mono text-xs text-slate-600">{r.cfdi_n_uuid || "-"}</td>
                    <td className="px-4 py-2.5">
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${CFDI_COLORS[r.cfdi_n_status] || "bg-slate-100"}`}>
                        {r.cfdi_n_status}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-xs text-slate-500">{r.cfdi_n_timbrado_at?.slice(0, 19) || "-"}</td>
                    <td className="px-4 py-2.5 flex items-center gap-2">
                      {r.cfdi_n_xml_url && (
                        <a
                          href={`${process.env.REACT_APP_BACKEND_URL}/api/ops/payroll/runs/${r.id}/cfdi-xml`}
                          target="_blank" rel="noreferrer"
                          className="text-xs text-blue-600 hover:underline flex items-center gap-1"
                          data-testid={`download-xml-${r.id}`}
                        >
                          <FileText className="w-3.5 h-3.5" /> XML
                        </a>
                      )}
                      {r.cfdi_n_pdf_url && (
                        <a
                          href={`${process.env.REACT_APP_BACKEND_URL}/api/ops/payroll/runs/${r.id}/cfdi-pdf`}
                          target="_blank" rel="noreferrer"
                          className="text-xs text-blue-600 hover:underline flex items-center gap-1"
                          data-testid={`download-pdf-${r.id}`}
                        >
                          <FileText className="w-3.5 h-3.5" /> Recibo
                        </a>
                      )}
                      {r.cfdi_n_status === "error" && isAllPayroll && (
                        <button
                          onClick={() => retryStamp(r.id)}
                          className="text-xs text-orange-600 hover:text-orange-700 flex items-center gap-1"
                          data-testid={`retry-stamp-${r.id}`}
                        >
                          <RotateCcw className="w-3.5 h-3.5" /> Reintentar
                        </button>
                      )}
                      {r.cfdi_n_status === "error" && r.cfdi_n_error_message && (
                        <span className="text-xs text-red-500 truncate max-w-[200px]" title={r.cfdi_n_error_message}>
                          {r.cfdi_n_error_message}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Modal de confirmacion */}
      {modal && (
        <ConfirmModal
          title={modal.label}
          message={modal.message}
          loading={actionLoading}
          onConfirm={() => doAction(modal.action, modal.label)}
          onCancel={() => setModal(null)}
        />
      )}
    </div>
  );
}
